import React from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Skeleton,
  Alert,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import BookIcon from "@mui/icons-material/Book";
import DevicesIcon from "@mui/icons-material/Devices";
import UpdateIcon from "@mui/icons-material/Update";
import LocalLibraryIcon from "@mui/icons-material/LocalLibrary";
import StarIcon from "@mui/icons-material/Star";
import { styled, keyframes } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useSubscriptionPlans from "../../hooks/api-request/useSubscriptionPlans";
import useCurrentSubscription from "../../hooks/api-request/useCurrentSubscription";

// Animations
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulse = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
  100% {
    transform: scale(1);
  }
`;

// Styled Components
const PageContainer = styled(Container)(({ theme }) => ({
  paddingTop: theme.spacing(12),
  paddingBottom: theme.spacing(12),
  position: "relative",
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: "50%",
    transform: "translateX(-50%)",
    width: "200%",
    height: "100%",
    background: `linear-gradient(135deg, ${theme.palette.primary.light}15 0%, ${theme.palette.background.default} 100%)`,
    zIndex: -1,
  },
}));

const StyledCard = styled(Card)(({ theme, isPopular = false }) => ({
  height: "100%",
  display: "flex",
  flexDirection: "column",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  position: "relative",
  borderRadius: theme.shape.borderRadius * 2,
  border: isPopular ? `2px solid ${theme.palette.primary.main}` : "none",
  backgroundColor: isPopular
    ? theme.palette.background.paper
    : theme.palette.grey[50],
  boxShadow: isPopular
    ? `0 8px 32px ${theme.palette.primary.main}20`
    : theme.shadows[2],
  "&:hover": {
    transform: "translateY(-8px)",
    boxShadow: isPopular
      ? `0 12px 48px ${theme.palette.primary.main}30`
      : theme.shadows[8],
  },
  animation: `${fadeIn} 0.6s ease-out forwards`,
  animationDelay: ({ index }) => `${index * 0.1}s`,
  opacity: 0,
}));

const PlanBadge = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: 20,
  right: -30,
  transform: "rotate(45deg)",
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.common.white,
  padding: "6px 28px",
  fontSize: "0.875rem",
  fontWeight: 600,
  letterSpacing: "0.5px",
  boxShadow: `0 2px 12px ${theme.palette.primary.main}40`,
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    background:
      "linear-gradient(45deg, transparent 0%, rgba(255,255,255,0.2) 100%)",
  },
}));

const BenefitCard = styled(Card)(({ theme }) => ({
  height: "100%",
  padding: theme.spacing(4),
  background: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius * 2,
  transition: "all 0.3s ease",
  border: `1px solid ${theme.palette.divider}`,
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: `0 8px 24px ${theme.palette.primary.main}15`,
    "& .benefit-icon": {
      transform: "scale(1.1)",
      color: theme.palette.primary.main,
    },
  },
  animation: `${fadeIn} 0.6s ease-out forwards`,
  animationDelay: ({ index }) => `${index * 0.1}s`,
  opacity: 0,
}));

const BenefitIcon = styled(Box)(({ theme }) => ({
  width: 64,
  height: 64,
  borderRadius: "50%",
  backgroundColor: theme.palette.primary.main,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  marginBottom: theme.spacing(2),
  transition: "all 0.3s ease",
  boxShadow: `0 4px 20px ${theme.palette.primary.main}30`,
  "& .MuiSvgIcon-root": {
    fontSize: 32,
    color: theme.palette.common.white,
    transition: "all 0.3s ease",
  },
  "&:hover": {
    transform: "scale(1.05)",
    boxShadow: `0 6px 24px ${theme.palette.primary.main}40`,
  },
}));

const PriceTag = styled(Box)(({ theme }) => ({
  display: "inline-flex",
  alignItems: "flex-start",
  marginBottom: theme.spacing(3),
  "& .currency": {
    fontSize: "1.5rem",
    fontWeight: 600,
    marginRight: theme.spacing(0.5),
    color: theme.palette.primary.main,
  },
  "& .amount": {
    fontSize: "3.5rem",
    fontWeight: 700,
    lineHeight: 1,
    color: theme.palette.primary.main,
  },
  "& .period": {
    fontSize: "1rem",
    color: theme.palette.text.secondary,
    marginLeft: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
}));

const FeatureList = styled(List)(({ theme }) => ({
  "& .MuiListItem-root": {
    paddingLeft: 0,
    paddingRight: 0,
  },
  "& .MuiListItemIcon-root": {
    minWidth: 32,
    color: theme.palette.success.main,
  },
  "& .MuiListItemText-primary": {
    fontSize: "0.9rem",
  },
}));

const SubscribeButton = styled(Button)(({ theme, isPopular }) => ({
  borderRadius: theme.shape.borderRadius * 1.5,
  padding: theme.spacing(1.5),
  fontWeight: 600,
  textTransform: "none",
  fontSize: "1rem",
  boxShadow: isPopular ? `0 4px 16px ${theme.palette.primary.main}40` : "none",
  "&:hover": {
    boxShadow: isPopular
      ? `0 6px 20px ${theme.palette.primary.main}60`
      : theme.shadows[4],
  },
  animation: isPopular ? `${pulse} 2s infinite` : "none",
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  position: "relative",
  display: "inline-block",
  marginBottom: theme.spacing(1),
  "&::after": {
    content: '""',
    position: "absolute",
    bottom: -8,
    left: 0,
    width: 60,
    height: 4,
    borderRadius: 2,
    backgroundColor: theme.palette.primary.main,
  },
}));

const Subscription = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === 'ps' || i18n.language === 'dr';
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const {
    data: plans,
    isLoading: plansLoading,
    error: plansError,
  } = useSubscriptionPlans();
  const { data: currentSubscription } = useCurrentSubscription();

  const handleSubscribe = (plan) => {
    navigate("/checkout", {
      state: {
        type: "SUBSCRIPTION",
        items: {
          id: plan.id,
          name: plan.name,
          price: plan.price,
          duration_days: plan.duration_days,
          description: plan.description,
          formatted_price: plan.formatted_price,
          duration_display: plan.duration_display,
          is_current: plan.is_current,
        },
      },
    });
  };

  const benefits = [
    {
      icon: BookIcon,
      title: t('subscription.benefits.unlimitedAccess.title'),
      description: t('subscription.benefits.unlimitedAccess.description'),
    },
    {
      icon: DevicesIcon,
      title: t('subscription.benefits.multiDevice.title'),
      description: t('subscription.benefits.multiDevice.description'),
    },
    {
      icon: UpdateIcon,
      title: t('subscription.benefits.regularUpdates.title'),
      description: t('subscription.benefits.regularUpdates.description'),
    },
    {
      icon: LocalLibraryIcon,
      title: t('subscription.benefits.exclusiveContent.title'),
      description: t('subscription.benefits.exclusiveContent.description'),
    },
  ];

  const formatDuration = (days) => {
    if (days === 30) return t('subscription.duration.month');
    if (days === 365) return t('subscription.duration.year');
    return t('subscription.duration.days', { days });
  };

  return (
    <PageContainer maxWidth="lg">
      {plansError && (
        <Alert
          severity="error"
          sx={{
            mb: 4,
            borderRadius: 2,
            boxShadow: theme.shadows[2],
          }}
        >
          {plansError?.message || t('subscription.errors.failedToLoad')}
        </Alert>
      )}

      <Box sx={{ textAlign: "center", mb: 8 }}>
        <SectionTitle variant="h2" component="h1" gutterBottom>
          {t('subscription.chooseYourPlan')}
        </SectionTitle>
        <Typography
          variant="h5"
          color="text.secondary"
          sx={{
            maxWidth: 600,
            mx: "auto",
            mb: 4,
            lineHeight: 1.6,
          }}
        >
          {t('subscription.subtitle')}
        </Typography>
      </Box>

      {/* Benefits Section */}
      <Grid container spacing={4} sx={{ mb: 12 }}>
        {benefits.map((benefit, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <BenefitCard index={index}>
              <Box sx={{ textAlign: "center" }}>
                <BenefitIcon className="benefit-icon">
                  <benefit.icon />
                </BenefitIcon>
                <Typography variant="h6" gutterBottom>
                  {benefit.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {benefit.description}
                </Typography>
              </Box>
            </BenefitCard>
          </Grid>
        ))}
      </Grid>

      {/* Subscription Plans */}
      <Grid container spacing={4} alignItems="stretch">
        {plansLoading ? (
          [...Array(3)].map((_, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Skeleton
                variant="rectangular"
                height={500}
                sx={{
                  borderRadius: 4,
                  transform: "scale(0.98)",
                }}
              />
            </Grid>
          ))
        ) : plans?.length > 0 ? (
          plans.map((plan, index) => (
            <Grid item xs={12} md={4} key={plan.id}>
              <StyledCard isPopular={index === 1} index={index}>
                {index === 1 && <PlanBadge>{t('subscription.mostPopular')}</PlanBadge>}
                <CardHeader
                  title={
                    <Box sx={{ textAlign: "center" }}>
                      <Typography variant="h5" component="div" gutterBottom>
                        {plan.name}
                      </Typography>
                      {index === 1 && (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 1,
                          }}
                        >
                          <StarIcon sx={{ color: "primary.main" }} />
                          <Typography variant="body2" color="primary">
                            {t('subscription.recommendedForYou')}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  }
                  sx={{ bgcolor: "transparent", pb: 0 }}
                />
                <CardContent
                  sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}
                >
                  <Box sx={{ textAlign: "center" }}>
                    <PriceTag>
                      <span className="currency">$</span>
                      <span className="amount">{Math.floor(plan.price)}</span>
                      {plan.price % 1 !== 0 && (
                        <span className="amount">
                          {(plan.price % 1).toFixed(2).substring(1)}
                        </span>
                      )}
                      <span className="period">
                        /{formatDuration(plan.duration_days)}
                      </span>
                    </PriceTag>
                  </Box>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      mb: 3,
                      textAlign: "center",
                      minHeight: 48,
                    }}
                  >
                    {plan.description}
                  </Typography>

                  <FeatureList>
                    {[
                      t('subscription.features.unlimitedAccess'),
                      t('subscription.features.mobileApp'),
                      t('subscription.features.newReleases'),
                      t('subscription.features.prioritySupport'),
                      t('subscription.features.cancelAnytime'),
                    ].map((feature, i) => (
                      <ListItem key={i}>
                        <ListItemIcon>
                          <CheckIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary={feature} />
                      </ListItem>
                    ))}
                  </FeatureList>

                  <Box sx={{ mt: "auto", pt: 3 }}>
                    <SubscribeButton
                      variant={index === 1 ? "contained" : "outlined"}
                      fullWidth
                      size="large"
                      onClick={() => handleSubscribe(plan)}
                      disabled={
                        currentSubscription?.subscription_status === "ACTIVE"
                      }
                      isPopular={index === 1}
                    >
                      {currentSubscription?.subscription_status === "ACTIVE"
                        ? t('subscription.currentlySubscribed')
                        : index === 1
                          ? t('subscription.getStartedNow')
                          : t('subscription.choosePlan')}
                    </SubscribeButton>
                  </Box>
                </CardContent>
              </StyledCard>
            </Grid>
          ))
        ) : (
          <Grid item xs={12}>
            <Alert
              severity="info"
              sx={{
                borderRadius: 2,
                boxShadow: theme.shadows[2],
                p: 2,
              }}
            >
              {t('subscription.noPlansAvailable')}
            </Alert>
          </Grid>
        )}
      </Grid>
    </PageContainer>
  );
};

export default Subscription;
